import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { Modal, Text, NumberInput, LsaCategoriesModal, i18n, tokens } from '@simplybenefits/sb-ui-library';
import { numberWithCommas } from '../../services/helpers.js';
import { setSpendingAccountMaximums, getLsaItems } from '../../actions/spendingAccounts.js';
import * as dates from "@simplybenefits/sb-dates";
import { calculateBalances } from './getProratedQuarterlyBalances.js';

class SetMaximumsModal extends PureComponent {
    state = {
        hsaAmount: 0,
        lsaAmount: 0,
        proratedHsa: 0,
        proratedLsa: 0,
        showLsaItemsModal: false
    };

    componentDidUpdate(_, prevState) {
        const { hsaAmount, lsaAmount } = this.state;
        const { proratedBy, eligibilityPeriodEnds: eligibilityDate, isQuarterlyAllowance, isProrated } = this.props.meta;

        if (prevState.hsaAmount !== hsaAmount || prevState.lsaAmount !== lsaAmount) {
            const { proratedHsa, proratedLsa } = calculateBalances(eligibilityDate, proratedBy, hsaAmount, lsaAmount, isQuarterlyAllowance, isProrated);

            this.setState({
                proratedHsa,
                proratedLsa
            });
        }
    }

    _handleAmountChange = (type = "HSA", value) => {
        const amountAvailable = this.props.meta.pooledAmountAvailable;
    
        // Ensure the value does not exceed the available amount
        const sanitizedValue = Math.min(Number(value), amountAvailable);
    
        // Calculate the difference
        const diff = amountAvailable - sanitizedValue;
    
        // Determine the updated state based on the type
        const updatedState = type === 'HSA'
            ? { hsaAmount: sanitizedValue, lsaAmount: diff }
            : { lsaAmount: sanitizedValue, hsaAmount: diff };
    
        this.setState(updatedState);
    };

    _validateForm = () => {
        let hsaAmountError = false;
        let lsaAmountError = false;

        const hsaAmount = Number(`${ this.state.hsaAmount }`.replace(',', ''));
        const lsaAmount = Number(`${ this.state.lsaAmount }`.replace(',', ''));

        if (hsaAmount + lsaAmount !== this.props.meta.pooledAmountAvailable) {
            // eslint-disable-next-line no-multi-assign
            hsaAmountError = lsaAmountError = i18n.t(tokens.labels.spendingAccounts.ensureMaximumEqualsAvailable);
        }

        this.setState({
            hsaAmountError,
            lsaAmountError
        });

        if (
            hsaAmountError ||
            lsaAmountError
        ) {
            return false;
        }

        return true;
    };

    _setMaximums = async () => {
        const isValid = await this._validateForm();

        if (!isValid) {
            return;
        }

        this.props.dispatch(setSpendingAccountMaximums(this.state.hsaAmount, this.state.lsaAmount));
    };

    render() {
        const { hsaAmount, lsaAmount } = this.state;
        const changeYear = dates.getUTC(null, 'YYYY');
        const effDateYear = dates.getUTC(this.props.meta.eligibilityPeriodEnds, 'YYYY');

        return (
            <Modal
                testid="setSpendingAccountMaximumsModal"
                open={true}
                title={ i18n.t(tokens.titles["set spending account maximums"]) }
                primaryButton={{
                    label: i18n.t(tokens.labels.spendingAccounts.setMaximums),
                    onClick: this._setMaximums
                }}
                loader={this.props.loader}
            >
                <Text small margin="0 0 10px 0">{ i18n.t(tokens.labels.spendingAccounts.pooledSpendingAccountNote) }</Text>

                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Text bold small margin="0 5px 0 0">{ i18n.t(tokens.labels.spendingAccounts.amountAvailable) } </Text>
                    <Text bold small>{ `$${ numberWithCommas(this.props.meta.pooledAmountAvailable.toFixed(2)) }` }</Text>
                </div>

                <NumberInput
                    label={ i18n.t(tokens.labels.spendingAccounts.healthSpendingMaximum) }
                    prefix="$"
                    value={Number(hsaAmount)}
                    max={Number(this.props.meta.pooledAmountAvailable)}
                    min={0}
                    allowLeadingZeros={false}
                    onChange={(e) => this._handleAmountChange('HSA', e.target.value)}
                    error={ this.state.hsaAmountError }
                    fullWidth
                />

                <NumberInput
                    label={ i18n.t(tokens.labels.spendingAccounts.lifestyleSpendingMaximum) }  
                    prefix="$ "
                    value={lsaAmount}
                    max={Number(this.props.meta.pooledAmountAvailable)}
                    min={0}
                    allowLeadingZeros={false}
                    onChange={(e) => this._handleAmountChange('LSA', e.target.value)}
                    error={ this.state.lsaAmountError }
                    fullWidth
                />

                <div
                    onClick={() => this.setState({ showLsaItemsModal: true })}
                    style={{ cursor: 'pointer' }}
                >
                    <Text tiny>{ i18n.t(tokens.labels.spendingAccounts.whatAmEligibleFor) } </Text>
                </div>

                {
                    this.state.showLsaItemsModal &&
                        <LsaCategoriesModal
                            open={ this.state.showLsaItemsModal }
                            getItems={ () => this.props.dispatch(getLsaItems()) }
                            whitelabel={ this.props.whitelabel || {} }
                            close={ () => {
                                this.setState({ showLsaItemsModal: false });
                            }}
                            hideDisabledLsaItems={true}
                        />
                }

                {
                    (this.props.meta.isProrated === 1 && effDateYear === changeYear) &&
                        <>
                            <Text small margin="10px 0 20px 0">
                                { i18n.t(tokens.labels.spendingAccounts.proratedPolicyNote, { proratedBy: this.props.meta.proratedBy })}
                            </Text>

                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Text bold small margin="0 5px 0 0">{ i18n.t(tokens.labels.spendingAccounts.proratedHealthSpendingBalance) }</Text>
                                <Text bold small>{ `$${ numberWithCommas(Math.ceil(this.state.proratedHsa)) }` }</Text>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <Text bold small margin="0 5px 0 0">{ i18n.t(tokens.labels.spendingAccounts.proratedLifestyleSpendingBalance) }</Text>
                                <Text bold small>{ `$${ numberWithCommas(Math.ceil(this.state.proratedLsa)) }` }</Text>
                            </div>
                        </>
                }

                {
                    (this.props.meta.isQuarterlyAllowance === 1) &&
                        <Text small margin="10px 0 20px 0">
                            { i18n.t(tokens.labels.spendingAccounts.quarterlyAllowanceNote) }
                        </Text>
                }
            </Modal>
        );
    }
}

export default connect()(SetMaximumsModal);